import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Title1,
    Body1,
    Button,
    Input,
    Spinner,
    MessageBar,
    MessageBarBody,
} from '@fluentui/react-components';
import {
    LockClosed24Regular,
    Checkmark24Regular,
    ErrorCircle24Regular,
} from '@fluentui/react-icons';
import { verifyToken, setPassword } from '../services/api';

export default function SetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get('token');
    const type = (searchParams.get('type') as 'invite' | 'reset') || 'invite';

    const [loading, setLoading] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');

    const [password, setPasswordValue] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        async function verifyOnLoad() {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const result = await verifyToken(token, type);
                if (result.valid) {
                    setTokenValid(true);
                    setUsername(result.username || '');
                    setEmail(result.email || '');
                } else {
                    setError(result.error || 'Invalid or expired link');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to verify link');
            } finally {
                setLoading(false);
            }
        }

        verifyOnLoad();
    }, [token, type]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validate passwords
        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setSubmitting(true);

        try {
            await setPassword(token!, type, password);
            setSuccess(true);
        } catch (err: any) {
            setError(err.message || 'Failed to set password');
        } finally {
            setSubmitting(false);
        }
    };

    const pageTitle = type === 'invite' ? 'Set Your Password' : 'Reset Your Password';
    const subtitle = type === 'invite'
        ? 'Welcome! Create a password to access your account.'
        : 'Enter a new password for your account.';

    // Loading state
    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
            }}>
                <Card style={{ padding: 40, textAlign: 'center' }}>
                    <Spinner size="large" label="Verifying link..." />
                </Card>
            </div>
        );
    }

    // No token provided
    if (!token) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
            }}>
                <Card style={{ padding: 40, maxWidth: 400, textAlign: 'center' }}>
                    <ErrorCircle24Regular style={{ color: '#c50f1f', marginBottom: 16 }} />
                    <Title1 style={{ marginBottom: 16 }}>Invalid Link</Title1>
                    <Body1 style={{ color: '#666', marginBottom: 24 }}>
                        This link is missing required information. Please use the link from your email.
                    </Body1>
                    <Button appearance="primary" onClick={() => navigate('/login')}>
                        Go to Login
                    </Button>
                </Card>
            </div>
        );
    }

    // Token invalid or expired
    if (!tokenValid && !loading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
            }}>
                <Card style={{ padding: 40, maxWidth: 400, textAlign: 'center' }}>
                    <ErrorCircle24Regular style={{ color: '#c50f1f', marginBottom: 16 }} />
                    <Title1 style={{ marginBottom: 16 }}>Link Expired</Title1>
                    <Body1 style={{ color: '#666', marginBottom: 24 }}>
                        {type === 'invite'
                            ? 'This invitation link has expired. Please contact your administrator for a new invite.'
                            : 'This password reset link has expired. Please request a new one.'}
                    </Body1>
                    <Button appearance="primary" onClick={() => navigate('/login')}>
                        Go to Login
                    </Button>
                </Card>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
            }}>
                <Card style={{ padding: 40, maxWidth: 400, textAlign: 'center' }}>
                    <Checkmark24Regular style={{ color: '#0e7a0d', marginBottom: 16, width: 48, height: 48 }} />
                    <Title1 style={{ marginBottom: 16 }}>Password Set!</Title1>
                    <Body1 style={{ color: '#666', marginBottom: 24 }}>
                        Your password has been set successfully. You can now log in with your new password.
                    </Body1>
                    <Button appearance="primary" onClick={() => navigate('/login')}>
                        Go to Login
                    </Button>
                </Card>
            </div>
        );
    }

    // Password form
    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
        }}>
            <Card style={{ padding: 40, maxWidth: 420 }}>
                {/* Logo */}
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <img
                        src="/edi-partners-icon.png"
                        alt="EDI Partners"
                        style={{ height: 60, marginBottom: 16 }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                        }}
                    />
                    <Title1>{pageTitle}</Title1>
                </div>

                <Body1 style={{ color: '#666', textAlign: 'center', marginBottom: 24 }}>
                    {subtitle}
                </Body1>

                {/* User info */}
                <div style={{
                    backgroundColor: '#f0f4f8',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 24,
                }}>
                    <Body1 style={{ fontWeight: 600, display: 'block' }}>{username}</Body1>
                    <Body1 style={{ color: '#666', fontSize: 13, display: 'block', marginTop: 4 }}>{email}</Body1>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <MessageBar intent="error" style={{ marginBottom: 16 }}>
                            <MessageBarBody>{error}</MessageBarBody>
                        </MessageBar>
                    )}

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                            New Password
                        </label>
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPasswordValue(e.target.value)}
                            placeholder="Enter new password"
                            contentBefore={<LockClosed24Regular />}
                            style={{ width: '100%' }}
                            required
                            minLength={8}
                        />
                        <span style={{ fontSize: 12, color: '#666' }}>
                            At least 8 characters
                        </span>
                    </div>

                    <div style={{ marginBottom: 24 }}>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                            Confirm Password
                        </label>
                        <Input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm password"
                            contentBefore={<LockClosed24Regular />}
                            style={{ width: '100%' }}
                            required
                        />
                    </div>

                    <Button
                        type="submit"
                        appearance="primary"
                        style={{ width: '100%' }}
                        disabled={submitting}
                    >
                        {submitting ? <Spinner size="tiny" /> : 'Set Password'}
                    </Button>
                </form>
            </Card>
        </div>
    );
}
